import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Plus, Edit, Trash2, X, Search, Upload, Download, Check, ChevronsUpDown, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination, ITEMS_PER_PAGE } from '@/components/Pagination';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

const BOM = ({ user }) => {
  const [boms, setBoms] = useState([]);
  const [filteredBoms, setFilteredBoms] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingBom, setEditingBom] = useState(null);
  const [formData, setFormData] = useState({
    product_code: '',
    product_name: '',
    raw_materials: [],
  });

  // Confirmation Dialog State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState('product_code');
  const [sortDir, setSortDir] = useState('asc');

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) return <ArrowUpDown size={14} className="ml-1 inline opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp size={14} className="ml-1 inline text-[hsl(145,60%,30%)]" />
      : <ArrowDown size={14} className="ml-1 inline text-[hsl(145,60%,30%)]" />;
  };

  useEffect(() => {
    if (searchTerm) {
      const filtered = boms.filter(b =>
        b.product_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.raw_materials?.some(m => m.material_name?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredBoms(filtered);
    } else {
      setFilteredBoms(boms);
    }
    setCurrentPage(1);
  }, [searchTerm, boms]);

  const fetchData = async () => {
    try {
      const [bomsRes, materialsRes] = await Promise.all([
        axios.get(`${API}/bom`),
        axios.get(`${API}/raw-materials`),
      ]);
      setBoms(bomsRes.data);
      setFilteredBoms(bomsRes.data);
      setMaterials(materialsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    const formDataObj = new FormData();
    formDataObj.append('file', file);

    try {
      const res = await axios.post(`${API}/bom/bulk-upload`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Upload complete: ${res.data.created} created, ${res.data.updated} updated`);
      if (res.data.errors?.length > 0) {
        toast.error(`${res.data.errors.length} errors occurred`);
      }
      setUploadDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = ['product_code', 'product_name', 'material_code_1', 'quantity_1', 'material_code_2', 'quantity_2', 'material_code_3', 'quantity_3'];
    const sampleData = ['PROD001', 'Sample Product', 'RM001', '10', 'RM002', '5', '', ''];
    const csvContent = [headers.join(','), sampleData.join(',')].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bom_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setFormData({
      product_code: '',
      product_name: '',
      raw_materials: [],
    });
    setEditingBom(null);
  };

  const handleEdit = (bom) => {
    setEditingBom(bom);
    setFormData({
      product_code: bom.product_code,
      product_name: bom.product_name,
      raw_materials: bom.raw_materials,
    });
    setDialogOpen(true);
  };

  const addMaterial = () => {
    setFormData({
      ...formData,
      raw_materials: [...formData.raw_materials, { material_id: '', material_name: '', quantity: '' }],
    });
  };

  const removeMaterial = (index) => {
    const updated = formData.raw_materials.filter((_, i) => i !== index);
    setFormData({ ...formData, raw_materials: updated });
  };

  const updateMaterial = (index, field, value) => {
    const updated = [...formData.raw_materials];
    updated[index][field] = value;

    if (field === 'material_id') {
      const mat = materials.find((m) => m.id === value);
      if (mat) {
        updated[index].material_name = mat.name;
      }
    }

    setFormData({ ...formData, raw_materials: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        ...formData,
        raw_materials: formData.raw_materials.map((rm) => ({
          ...rm,
          quantity: parseFloat(rm.quantity),
        })),
      };

      if (editingBom) {
        await axios.put(`${API}/bom/${editingBom.id}`, data);
        toast.success('BOM updated successfully');
      } else {
        await axios.post(`${API}/bom`, data);
        toast.success('BOM created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    }
  };

  const handleDeleteClick = (bom) => {
    setItemToDelete(bom);
    setConfirmOpen(true);
  };

  const executeDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/bom/${itemToDelete.id}`);
      toast.success('BOM deleted successfully');
      fetchData();
      setConfirmOpen(false);
    } catch (error) {
      toast.error('Failed to delete BOM');
    } finally {
      setIsDeleting(false);
      setItemToDelete(null);
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="bom-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-4xl font-semibold text-[hsl(145,60%,10%)] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Bill of Materials</h1>
          <p className="text-sm text-[hsl(145,20%,40%)] mt-2">Define product recipes and material requirements</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="upload-bom-btn">
                <Upload size={16} className="mr-2" />
                Upload Excel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Upload BOMs from Excel</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-stone-50 rounded-sm text-sm">
                  <p className="font-medium mb-2">Required Format:</p>
                  <ul className="text-xs text-stone-600 space-y-1">
                    <li>• Column 1: <strong>product_code</strong> (required)</li>
                    <li>• Column 2: <strong>product_name</strong> (required)</li>
                    <li>• Columns 3+: Pairs of <strong>material_code</strong> and <strong>quantity</strong></li>
                  </ul>
                  <p className="text-xs text-stone-500 mt-2">
                    Example: product_code | product_name | RM001 | 10 | RM002 | 5
                  </p>
                </div>

                <Button variant="outline" size="sm" onClick={downloadTemplate} className="w-full">
                  <Download size={14} className="mr-2" /> Download Template
                </Button>

                <div>
                  <Label htmlFor="bom-file-upload">Select Excel File</Label>
                  <Input
                    id="bom-file-upload"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="mt-1"
                  />
                </div>

                {uploading && <p className="text-sm text-center text-stone-500">Uploading...</p>}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white" data-testid="add-bom-button">
                <Plus size={16} className="mr-2" />
                Add BOM
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingBom ? 'Edit BOM' : 'Add New BOM'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="bom-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="product_code">Product Code</Label>
                    <Input
                      id="product_code"
                      value={formData.product_code}
                      onChange={(e) => setFormData({ ...formData, product_code: e.target.value })}
                      required
                      data-testid="input-product-code"
                    />
                  </div>
                  <div>
                    <Label htmlFor="product_name">Product Name</Label>
                    <Input
                      id="product_name"
                      value={formData.product_name}
                      onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                      required
                      data-testid="input-product-name"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label>Raw Materials</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addMaterial} data-testid="add-material-to-bom">
                      <Plus size={14} className="mr-1" /> Add Material
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {formData.raw_materials.map((rm, index) => (
                      <div key={index} className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Label>Material</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                className="w-full justify-between font-normal"
                                data-testid={`select-material-${index}`}
                              >
                                {rm.material_id
                                  ? materials.find((mat) => mat.id === rm.material_id)?.name + ` (${materials.find((mat) => mat.id === rm.material_id)?.code})`
                                  : "Select material"}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[300px] p-0" align="start">
                              <Command>
                                <CommandInput placeholder="Search material..." />
                                <CommandList>
                                  <CommandEmpty>No material found.</CommandEmpty>
                                  <CommandGroup>
                                    {materials.map((mat) => (
                                      <CommandItem
                                        key={mat.id}
                                        value={mat.name + " " + mat.code}
                                        onSelect={() => {
                                          updateMaterial(index, 'material_id', mat.id);
                                          // Close is handled by clicking off or can add state
                                        }}
                                      >
                                        <Check className={`mr-2 h-4 w-4 ${rm.material_id === mat.id ? 'opacity-100' : 'opacity-0'}`} />
                                        {mat.name} ({mat.code})
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="w-32">
                          <Label>Quantity</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={rm.quantity}
                            onChange={(e) => updateMaterial(index, 'quantity', e.target.value)}
                            required
                            data-testid={`input-quantity-${index}`}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMaterial(index)}
                          data-testid={`remove-material-${index}`}
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white" data-testid="submit-bom-button">
                    {editingBom ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(145,20%,40%)]" size={18} />
          <Input
            placeholder="Search by product code, name, or material..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="bom-search"
          />
        </div>
        {searchTerm && (
          <p className="text-xs text-stone-500 mt-1">
            Found {filteredBoms.length} of {boms.length} BOMs
          </p>
        )}
      </div>

      <div className="bg-white rounded-sm border border-stone-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="bom-table">
            <thead className="bg-stone-50 border-b border-stone-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider cursor-pointer select-none hover:text-stone-900" onClick={() => toggleSort('product_code')}>Product Code <SortIcon column="product_code" /></th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider cursor-pointer select-none hover:text-stone-900" onClick={() => toggleSort('product_name')}>Product Name <SortIcon column="product_name" /></th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider cursor-pointer select-none hover:text-stone-900" onClick={() => toggleSort('materials_count')}>Materials Required <SortIcon column="materials_count" /></th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {[...filteredBoms].sort((a, b) => {
                let aVal, bVal;
                if (sortKey === 'materials_count') {
                  aVal = (a.raw_materials || []).length;
                  bVal = (b.raw_materials || []).length;
                } else {
                  aVal = String(a[sortKey] || '').toLowerCase();
                  bVal = String(b[sortKey] || '').toLowerCase();
                }
                if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
                return 0;
              }).slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((bom) => (
                <tr key={bom.id} className="hover:bg-stone-50/50 transition-colors" data-testid={`bom-row-${bom.product_code}`}>
                  <td className="px-6 py-3 text-sm font-medium text-[hsl(145,60%,10%)] whitespace-nowrap">{bom.product_code}</td>
                  <td className="px-6 py-3 text-sm text-[hsl(145,60%,10%)]">{bom.product_name}</td>
                  <td className="px-6 py-3">
                    <div className="space-y-1">
                      {bom.raw_materials.map((rm, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <span className="text-[hsl(145,60%,10%)]">{rm.material_name}</span>
                          <span className="text-stone-300">×</span>
                          <span className="font-mono text-[hsl(145,20%,40%)]">{rm.quantity}</span>
                        </div>
                      ))}
                      {bom.raw_materials.length === 0 && (
                        <span className="text-xs text-stone-400 italic">No materials</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(bom)} data-testid={`edit-bom-${bom.product_code}`}>
                        <Edit size={14} />
                      </Button>
                      {user?.role === 'admin' && (
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(bom)} data-testid={`delete-bom-${bom.product_code}`}>
                          <Trash2 size={14} className="text-red-600" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={currentPage} totalItems={filteredBoms.length} onPageChange={setCurrentPage} />
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={executeDelete}
        title={`Delete BOM for: ${itemToDelete?.product_name}?`}
        description="This action cannot be undone. This bill of materials will be removed from the system."
        confirmText="Delete BOM"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default BOM;